import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Cropper from 'react-easy-crop';
import { Loader2, RotateCcw, RotateCw, ZoomIn, ZoomOut } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import {
  DEFAULT_CROP_ASPECT,
  DEFAULT_CROP_ROTATION,
  DEFAULT_CROP_ZOOM,
  MAX_CROP_ZOOM,
  MAX_IMAGE_UPLOAD_BYTES,
  MIN_CROP_ZOOM,
  createObjectUrl,
  getCroppedImageFile,
  resolveOutputMimeType,
  revokeObjectUrl,
} from '../lib/imageCrop';

const INITIAL_CROP = { x: 0, y: 0 };

/**
 * Reusable image crop dialog: move/resize crop, zoom, rotate, reset, preview, apply/cancel.
 * Does not upload — callers receive a File via onApply and use existing upload APIs.
 */
export default function ImageCropper({
  open,
  imageSrc,
  fileName = 'cropped-image.jpg',
  mimeType,
  aspect = DEFAULT_CROP_ASPECT,
  maxBytes = MAX_IMAGE_UPLOAD_BYTES,
  title = 'Crop image',
  onCancel,
  onApply,
}) {
  const [crop, setCrop] = useState(INITIAL_CROP);
  const [zoom, setZoom] = useState(DEFAULT_CROP_ZOOM);
  const [rotation, setRotation] = useState(DEFAULT_CROP_ROTATION);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [mode, setMode] = useState('edit'); // 'edit' | 'preview'
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const resetTransforms = useCallback(() => {
    setCrop(INITIAL_CROP);
    setZoom(DEFAULT_CROP_ZOOM);
    setRotation(DEFAULT_CROP_ROTATION);
    setError('');
  }, []);

  const clearPreview = useCallback(() => {
    setPreviewUrl((prev) => {
      revokeObjectUrl(prev);
      return null;
    });
    setPreviewFile(null);
  }, []);

  useEffect(() => {
    if (!open) {
      resetTransforms();
      clearPreview();
      setMode('edit');
      setCroppedAreaPixels(null);
      setProcessing(false);
      setError('');
      return undefined;
    }
    resetTransforms();
    clearPreview();
    setMode('edit');
    setCroppedAreaPixels(null);
    setError('');
    return undefined;
  }, [open, imageSrc, resetTransforms, clearPreview]);

  useEffect(() => () => {
    clearPreview();
  }, [clearPreview]);

  const onCropComplete = useCallback((_croppedArea, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleCancel = () => {
    if (processing) return;
    clearPreview();
    onCancel?.();
  };

  const handleApplyCrop = async () => {
    if (processing || !imageSrc || !croppedAreaPixels) {
      setError('Adjust the crop area before applying.');
      return;
    }
    setProcessing(true);
    setError('');
    try {
      const outputMime = mimeType || resolveOutputMimeType({ name: fileName, type: mimeType || '' });
      const file = await getCroppedImageFile(imageSrc, croppedAreaPixels, {
        fileName,
        mimeType: outputMime,
        rotation,
        maxBytes,
      });
      clearPreview();
      const url = createObjectUrl(file);
      setPreviewFile(file);
      setPreviewUrl(url);
      setMode('preview');
    } catch (err) {
      setError(err?.message || 'Could not crop this image. Please try another file.');
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirm = async () => {
    if (processing || !previewFile) return;
    setProcessing(true);
    setError('');
    try {
      await onApply?.(previewFile);
    } catch (err) {
      setError(err?.message || 'Failed to use cropped image.');
      setProcessing(false);
      return;
    }
    setProcessing(false);
  };

  const handleEditAgain = () => {
    if (processing) return;
    clearPreview();
    setMode('edit');
    setError('');
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) handleCancel(); }}>
      <DialogContent
        className="max-w-lg sm:max-w-xl gap-0 p-0 overflow-hidden"
        showCloseButton={!processing}
        onPointerDownOutside={(e) => { if (processing) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (processing) e.preventDefault(); }}
      >
        <DialogHeader className="px-4 pt-4 pb-2 sm:px-5">
          <DialogTitle className="text-base sm:text-lg text-slate-900">{title}</DialogTitle>
          <DialogDescription className="text-xs sm:text-sm text-slate-500">
            {mode === 'edit'
              ? 'Move and resize the crop area, then apply to preview the result before upload.'
              : 'Preview your cropped image. Confirm to continue, or edit again.'}
          </DialogDescription>
        </DialogHeader>

        <div className="px-4 sm:px-5 pb-3 space-y-3">
          {mode === 'edit' ? (
            <>
              <div className="relative h-[240px] sm:h-[300px] w-full overflow-hidden rounded-xl bg-slate-900">
                {imageSrc ? (
                  <Cropper
                    image={imageSrc}
                    crop={crop}
                    zoom={zoom}
                    rotation={rotation}
                    aspect={aspect}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onRotationChange={setRotation}
                    onCropComplete={onCropComplete}
                    objectFit="contain"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-sm text-slate-300">
                    No image selected
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <Label htmlFor="image-crop-zoom" className="text-xs font-medium text-slate-700">
                      Zoom
                    </Label>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        aria-label="Zoom out"
                        disabled={processing || zoom <= MIN_CROP_ZOOM}
                        onClick={() => setZoom((z) => Math.max(MIN_CROP_ZOOM, Number((z - 0.1).toFixed(2))))}
                      >
                        <ZoomOut className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        aria-label="Zoom in"
                        disabled={processing || zoom >= MAX_CROP_ZOOM}
                        onClick={() => setZoom((z) => Math.min(MAX_CROP_ZOOM, Number((z + 0.1).toFixed(2))))}
                      >
                        <ZoomIn className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <Slider
                    id="image-crop-zoom"
                    aria-label="Zoom"
                    min={MIN_CROP_ZOOM}
                    max={MAX_CROP_ZOOM}
                    step={0.05}
                    value={[zoom]}
                    disabled={processing}
                    onValueChange={(v) => setZoom(v[0])}
                  />
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-lg"
                    aria-label="Rotate left"
                    disabled={processing}
                    onClick={() => setRotation((r) => (r - 90 + 360) % 360)}
                  >
                    <RotateCcw className="h-4 w-4" />
                    Rotate left
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-lg"
                    aria-label="Rotate right"
                    disabled={processing}
                    onClick={() => setRotation((r) => (r + 90) % 360)}
                  >
                    <RotateCw className="h-4 w-4" />
                    Rotate right
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="rounded-lg"
                    aria-label="Reset crop"
                    disabled={processing}
                    onClick={resetTransforms}
                  >
                    Reset
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center gap-3">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Cropped preview"
                  className="max-h-[280px] w-auto max-w-full rounded-xl border border-slate-200 object-contain bg-slate-50"
                />
              ) : null}
              {previewFile ? (
                <p className="text-xs text-slate-500">
                  {previewFile.name}
                  {' · '}
                  {(previewFile.size / 1024).toFixed(1)}
                  KB
                </p>
              ) : null}
            </div>
          )}

          {error ? (
            <p className="text-xs text-red-600" role="alert">{error}</p>
          ) : null}
        </div>

        <DialogFooter className="border-t border-slate-100 px-4 py-3 sm:px-5 gap-2">
          <Button
            type="button"
            variant="outline"
            className="rounded-xl"
            onClick={handleCancel}
            disabled={processing}
            aria-label="Cancel crop"
          >
            Cancel
          </Button>
          {mode === 'edit' ? (
            <Button
              type="button"
              className="rounded-xl bg-[#1B6EF3] hover:bg-[#1559cc]"
              onClick={handleApplyCrop}
              disabled={processing || !imageSrc}
              aria-label="Apply crop"
            >
              {processing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Processing…
                </>
              ) : (
                'Apply Crop'
              )}
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                className="rounded-xl"
                onClick={handleEditAgain}
                disabled={processing}
                aria-label="Edit crop again"
              >
                Edit again
              </Button>
              <Button
                type="button"
                className="rounded-xl bg-[#1B6EF3] hover:bg-[#1559cc]"
                onClick={handleConfirm}
                disabled={processing || !previewFile}
                aria-label="Confirm cropped image"
              >
                {processing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Working…
                  </>
                ) : (
                  'Use cropped image'
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

ImageCropper.propTypes = {
  open: PropTypes.bool.isRequired,
  imageSrc: PropTypes.string,
  fileName: PropTypes.string,
  mimeType: PropTypes.string,
  aspect: PropTypes.number,
  maxBytes: PropTypes.number,
  title: PropTypes.string,
  onCancel: PropTypes.func,
  onApply: PropTypes.func,
};
